import {
  createRootRouteWithContext,
  createRoute,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';

import { authErrorSchema } from '@/auth/auth-errors';
import type { AuthContextProps } from '@/auth/auth-provider';
import AppLayout from '@/components/layout/app-layout';
import Home from '@/routes/home';
import Login from '@/routes/login';
import Overview from '@/routes/overview';
import { paths } from '@/routes/paths';
import ResultDetail from '@/routes/result';
import Root from '@/routes/root';
import SubmitWizard from '@/routes/submit';

interface RouterContext {
  auth?: AuthContextProps;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Root,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    throw redirect({ to: context.auth?.isAuthenticated ? paths.app : paths.login });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // An unrecognised value degrades to no banner rather than failing the route.
  validateSearch: z.object({
    error: authErrorSchema.optional().catch(undefined),
  }),
  beforeLoad: ({ context }) => {
    if (context.auth?.isAuthenticated) {
      throw redirect({ to: paths.app });
    }
  },
  component: Login,
});

// Defensive: consumeLoginRedirect rewrites /auth/login pre-render. This catches
// bookmarked or replayed URLs that would otherwise 404.
const authLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/login',
  beforeLoad: () => {
    throw redirect({ to: '/' });
  },
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  beforeLoad: ({ context }) => {
    if (!context.auth?.isAuthenticated) {
      throw redirect({ to: paths.login });
    }
  },
  component: () => (
    <AppLayout>
      <Outlet />
    </AppLayout>
  ),
});

const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: Home,
});

const submitRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'submit',
  validateSearch: z.object({
    mode: z.enum(['file', 'questions']).default('file'),
  }),
  component: SubmitWizard,
});

const overviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'overview',
  component: Overview,
});

const resultRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'overview/$id',
  component: ResultDetail,
});

export const routes = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  authLoginRoute,
  appRoute.addChildren([homeRoute, submitRoute, overviewRoute, resultRoute]),
]);
